// Eskom PARCC — Data Safety (WS4). Admin-only page over the rolling snapshot
// system: every 30 minutes the service captures ALL work-product tables to
// Cloud Storage (kept 14 days); this page lists the snapshots, takes one on
// demand, and restores a chosen snapshot behind a server-computed dry-run
// preview + typed-RESTORE confirm. A restore is state-restoring for rows the
// snapshot knows and additive-safe for rows it doesn't (post-snapshot rows are
// left untouched and reported); a pre-restore snapshot is always taken first.
import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Button,
  DataTable,
  EmptyState,
  LoadingState,
  ErrorState,
  useToast,
  type Column,
  Pager,
  usePagination,
} from '../../components/ui';
import { adminApi, type SnapshotRunRow, type RestorePlanSummary } from '../../lib/adminApi';

/** SAST timestamp (the team works in Johannesburg; stamps are UTC). */
function formatSast(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-ZA', {
    timeZone: 'Africa/Johannesburg',
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(d);
}

const TRIGGER_LABEL: Record<string, string> = {
  auto: 'Automatic',
  manual: 'Manual',
  'pre-restore': 'Pre-restore',
};

function countsLine(counts: Record<string, number> | null): string {
  if (!counts) return '—';
  const tx = counts.transactions ?? 0;
  const wp = counts.workingPapers ?? 0;
  const f = counts.findings ?? 0;
  return `${tx.toLocaleString('en-ZA')} tx · ${wp.toLocaleString('en-ZA')} WPs · ${f.toLocaleString('en-ZA')} findings`;
}

/** The dry-run plan, summarised for the confirm panel. */
function planLines(plan: RestorePlanSummary): string[] {
  return [
    `Transactions: ${plan.transactions.update} updated, ${plan.transactions.insert} re-created, ${plan.transactions.unchanged} unchanged; ${plan.transactions.extrasUntouched} newer left untouched.`,
    `Working papers: ${plan.workingPapers.update} updated, ${plan.workingPapers.insert} re-created, ${plan.workingPapers.unchanged} unchanged.`,
    `CES sheets: ${plan.cesRows.replaceTransactions.length} transaction(s) restored (${plan.cesRows.rowsToWrite} rows); ${plan.cesRows.unchangedTransactions} unchanged.`,
    `Findings: ${plan.findings.replaceTransactions.length} transaction(s) restored (${plan.findings.rowsToWrite} lines); ${plan.findings.unchangedTransactions} unchanged.`,
    `QA notes: ${plan.qaNotes.update} updated, ${plan.qaNotes.insert} re-created.`,
    `RFI register: ${plan.rfiRegister.replace ? `replaced (${plan.rfiRegister.rows} rows)` : 'unchanged'}; RTM sign-off: ${plan.rtmSignoff.upsert} division(s).`,
  ];
}

export function DataSafetyPage(): React.JSX.Element {
  const { show } = useToast();
  const [runs, setRuns] = useState<SnapshotRunRow[] | null>(null);
  const [lastOkAt, setLastOkAt] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<SnapshotRunRow | null>(null);
  const [plan, setPlan] = useState<RestorePlanSummary | null>(null);
  const [planError, setPlanError] = useState('');
  const [confirmText, setConfirmText] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const r = await adminApi.snapshots();
      setRuns(r.runs);
      setLastOkAt(r.lastOkAt);
    } catch (err) {
      setRuns([]);
      setError(err instanceof Error ? err.message : 'Failed to load the snapshots.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleSnapshotNow(): Promise<void> {
    setBusy(true);
    try {
      const r = await adminApi.snapshotNow();
      show(`Snapshot taken (${countsLine(r.counts)}).`, 'success');
      await load();
    } catch (err) {
      show(err instanceof Error ? err.message : 'Snapshot failed.', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function openRestore(run: SnapshotRunRow): Promise<void> {
    setRestoreTarget(run);
    setPlan(null);
    setPlanError('');
    setConfirmText('');
    try {
      const r = await adminApi.restoreSnapshotDryRun(run.id);
      setPlan(r.plan);
    } catch (err) {
      setPlanError(err instanceof Error ? err.message : 'Could not compute the restore preview.');
    }
  }

  async function applyRestore(): Promise<void> {
    if (!restoreTarget || confirmText.trim() !== 'RESTORE') return;
    setBusy(true);
    try {
      const r = await adminApi.restoreSnapshot(restoreTarget.id);
      show(
        `Restored the snapshot of ${formatSast(restoreTarget.takenAt)}. A pre-restore safety snapshot was taken first.`,
        'success',
      );
      void r;
      setRestoreTarget(null);
      setPlan(null);
      setConfirmText('');
      await load();
    } catch (err) {
      show(err instanceof Error ? err.message : 'Restore failed.', 'error');
    } finally {
      setBusy(false);
    }
  }

  // Two weeks of half-hourly snapshots is ~670 rows — page them in both themes.
  const pagination = usePagination(runs ?? []);

  const columns: Column<SnapshotRunRow>[] = [
    {
      key: 'takenAt',
      header: 'Taken (SAST)',
      accessor: (r) => r.takenAt,
      render: (r) => formatSast(r.takenAt),
    },
    {
      key: 'trigger',
      header: 'Trigger',
      accessor: (r) => r.trigger,
      render: (r) => <span className="badge">{TRIGGER_LABEL[r.trigger] ?? r.trigger}</span>,
    },
    {
      key: 'counts',
      header: 'Contents',
      accessor: (r) => countsLine(r.counts),
      render: (r) => countsLine(r.counts),
    },
    {
      key: 'status',
      header: 'Status',
      accessor: (r) => r.status,
      render: (r) =>
        r.status === 'failed' ? (
          <span className="badge" title={r.error ?? ''}>
            Failed
          </span>
        ) : (
          <span className="badge">{r.status === 'ok' ? 'OK' : r.status}</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      accessor: () => '',
      render: (r) =>
        r.status === 'ok' ? (
          <Button
            variant="ghost"
            onClick={() => void openRestore(r)}
            disabled={busy}
            data-testid={`restore-open-${r.id}`}
          >
            Restore…
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <Card>
        <div className="section-head">
          <div>
            <h3>Data safety</h3>
            <div className="small">
              Snapshots are taken every 30 minutes and kept for 14 days. Each captures all review
              work — transactions, working papers, CES sheets, findings and QA notes — so an admin
              can restore recent state after a data fault. Last snapshot:{' '}
              <strong>{lastOkAt ? `${formatSast(lastOkAt)} (SAST)` : 'none yet'}</strong>.
            </div>
          </div>
          <div className="actions">
            <Button
              variant="primary"
              onClick={() => void handleSnapshotNow()}
              disabled={busy}
              data-testid="snapshot-now"
            >
              {busy ? 'Working…' : 'Snapshot now'}
            </Button>
          </div>
        </div>

        {runs === null ? (
          <LoadingState message="Loading the snapshots…" />
        ) : error ? (
          <ErrorState message="Could not load the snapshots." detail={error} />
        ) : runs.length === 0 ? (
          <EmptyState message="No snapshots yet — the first automatic run will appear here." />
        ) : (
          <>
            <DataTable
              columns={columns}
              rows={pagination.slice}
              getRowKey={(r) => r.id}
              emptyMessage="No snapshots."
            />
            <Pager pagination={pagination} noun="snapshot" />
          </>
        )}
      </Card>

      {restoreTarget && (
        <Card>
          <div className="section-head">
            <div>
              <h3>Restore the snapshot of {formatSast(restoreTarget.takenAt)}</h3>
              <div className="small">
                A safety snapshot is taken automatically before restoring. Items created after this
                snapshot are left in place and reported — a restore never silently deletes newer
                work.
              </div>
            </div>
            <div className="actions">
              <Button
                variant="ghost"
                onClick={() => {
                  setRestoreTarget(null);
                  setPlan(null);
                  setConfirmText('');
                }}
                disabled={busy}
              >
                Cancel
              </Button>
            </div>
          </div>

          {planError ? (
            <ErrorState message="Could not compute the restore preview." detail={planError} />
          ) : !plan ? (
            <LoadingState message="Computing what this restore would change…" />
          ) : (
            <>
              <ul
                className="small"
                style={{ margin: '4px 0 12px', paddingLeft: 18 }}
                data-testid="restore-plan"
              >
                {planLines(plan).map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              <div className="actions" style={{ gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
                  <span className="small">Type RESTORE to confirm</span>
                  <input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="RESTORE"
                    disabled={busy}
                    aria-label="Type RESTORE to confirm restoring this snapshot"
                    data-testid="restore-confirm"
                  />
                </label>
                <Button
                  variant="primary"
                  onClick={() => void applyRestore()}
                  disabled={busy || confirmText.trim() !== 'RESTORE'}
                  data-testid="restore-apply"
                >
                  {busy ? 'Restoring…' : 'Restore this snapshot'}
                </Button>
              </div>
            </>
          )}
        </Card>
      )}
    </>
  );
}
